import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} NojuPdpStickyAtcRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} stickyAtcButton
 */

const MOBILE_QUERY = '(max-width: 749px)';

/** Floating chat launchers to hide while the size sheet is open (NOJU uses <shopify-chat>). */
const CHAT_LAUNCHER_SELECTORS = [
  'shopify-chat',
  'inbox-online-store-chat',
  '#shopify-chat',
  '#ShopifyChat',
  '#dummy-chat-button-iframe',
  '#gorgias-chat-container',
  '#tidio-chat',
  '.tawk-min-container',
  '#launcher',
  '#intercom-container',
  '.intercom-lightweight-app',
  '#crisp-chatbox',
  'iframe[title*="chat" i]',
  '[id*="chat" i]',
  '[class*="chat-launcher" i]',
  '[class*="chat-widget" i]',
];

/** Fixed bottom overlays the bar must not collide with. */
const BOTTOM_OVERLAY_SELECTORS = [
  '#shopify-pc__banner',
  '#CybotCookiebotDialog',
  '#onetrust-banner-sdk',
  '.cc-window',
  '[data-cookie-banner]',
];

/**
 * Mobile sticky add-to-cart bar for the NOJU PDP.
 * - Visible only on mobile (< 750px) after the main ATC block has scrolled above the viewport.
 * - Mirrors label/mode/price/size from the main buy box (variant changes, sold out / notify).
 * - Click proxies the main ATC button (same add-to-cart path / cart drawer).
 * - Tétaro layout (2026-10-13): size button + buy button; without a complete size selection
 *   a bottom size sheet opens. Available size: selects it and adds. Sold-out size: selects it
 *   and opens Notify me (main button in notify mode). Core Set: one chip row per garment.
 * - Offsets above bottom overlays (cookie banner); hides behind tall consent dialogs.
 *
 * @extends {Component<NojuPdpStickyAtcRefs>}
 */
class NojuPdpStickyAtc extends Component {
  requiredRefs = ['stickyBar', 'stickyAtcButton'];

  /** @type {IntersectionObserver | null} */
  #atcIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  #isStuck = false;
  #wantsStuck = false;
  #hiddenByFooter = false;
  #hiddenByOverlay = false;

  /** @type {number | undefined} */
  #overlayTimer = undefined;

  /** @type {number | undefined} */
  #highlightTimer = undefined;

  /** @type {MediaQueryList | null} */
  #mobileQuery = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #lastOpener = null;

  #sheetOpen = false;

  /** @type {Array<{ el: HTMLElement, display: string, priority: string }>} */
  #hiddenChat = [];

  /** Hide floating chat launchers (fixed/sticky, outside this component). */
  #hideChat() {
    this.#showChat();
    const seen = new Set();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    CHAT_LAUNCHER_SELECTORS.forEach((selector) => {
      let nodes;
      try {
        nodes = document.querySelectorAll(selector);
      } catch {
        return;
      }
      nodes.forEach((el) => {
        if (!(el instanceof HTMLElement) || seen.has(el) || this.contains(el)) return;
        if (el === document.body || el === document.documentElement) return;
        const tag = el.tagName.toLowerCase();
        const known = tag === 'shopify-chat' || tag === 'inbox-online-store-chat';
        if (!known) {
          // Generic match: only floating elements docked near the bottom edge.
          const style = getComputedStyle(el);
          if (style.position !== 'fixed' && style.position !== 'sticky') return;
          const rect = el.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0 || rect.width > vw * 0.9) return;
          if (rect.bottom < vh * 0.5) return;
        }
        seen.add(el);
        this.#hiddenChat.push({
          el,
          display: el.style.getPropertyValue('display'),
          priority: el.style.getPropertyPriority('display'),
        });
        el.style.setProperty('display', 'none', 'important');
      });
    });
  }

  #showChat() {
    this.#hiddenChat.forEach(({ el, display, priority }) => {
      if (display) el.style.setProperty('display', display, priority);
      else el.style.removeProperty('display');
    });
    this.#hiddenChat = [];
  }

  connectedCallback() {
    super.connectedCallback();

    document.getElementById('noju-sticky-size-styles')?.remove();

    this.#mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.#setupIntersectionObservers();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    section?.addEventListener('change', this.#onSectionChange, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#onCartComplete, { signal });

    this.refs.stickyAtcButton.addEventListener('click', this.#onStickyClick, { signal });
    this.querySelector('[data-noju-sticky-size-button]')?.addEventListener('click', this.#onSizeButtonClick, { signal });
    this.#getSheet()?.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('[data-noju-sheet-close]')) this.#closeSheet();
      },
      { signal }
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#sheetOpen) this.#closeSheet();
      },
      { signal }
    );
    window.addEventListener('resize', this.#onResize, { signal });
    this.#mobileQuery.addEventListener('change', this.#onResize, { signal });

    this.#syncFromMainAtc();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#atcIntersectionObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
    window.clearInterval(this.#overlayTimer);
    window.clearTimeout(this.#highlightTimer);
    this.#clearOffsets();
    if (this.#sheetOpen) document.body.style.overflow = '';
    document.documentElement.classList.remove('noju-sb-sheet-open');
    this.#showChat();
  }

  #onResize = () => {
    this.#applyVisibility();
  };

  #setupIntersectionObservers() {
    const section = this.closest('.shopify-section');
    const atcBlock = section?.querySelector('.noju-pdp__block--atc');
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!atcBlock) return;

    this.#atcIntersectionObserver = new IntersectionObserver(() => {
      this.#wantsStuck = this.#isAtcAboveViewport(atcBlock);
      this.#applyVisibility();
    });
    this.#atcIntersectionObserver.observe(atcBlock);

    if (footer) {
      const footerSentinel =
        document.querySelector('.footer-localization-section') ??
        document.querySelector('.noju-footer-links-section') ??
        footer;

      this.#footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;
          this.#hiddenByFooter = entry.isIntersecting;
          this.#applyVisibility();
        },
        { rootMargin: '0px 0px 80px 0px', threshold: 0 }
      );
      this.#footerObserver.observe(footerSentinel);
    }

    this.#wantsStuck = this.#isAtcAboveViewport(atcBlock);
    this.#applyVisibility();
  }

  /** @param {Element} atcBlock */
  #isAtcAboveViewport(atcBlock) {
    return atcBlock.getBoundingClientRect().bottom < 0;
  }

  #applyVisibility() {
    const isMobile = this.#mobileQuery?.matches ?? false;
    if (isMobile && this.#wantsStuck && !this.#hiddenByFooter) {
      this.#updateOverlayOffset();
      if (this.#hiddenByOverlay) {
        this.#hide(true);
      } else {
        this.#show();
      }
      this.#startOverlayWatch();
    } else {
      this.#stopOverlayWatch();
      this.#hide();
    }
  }

  #startOverlayWatch() {
    if (this.#overlayTimer) return;
    this.#overlayTimer = window.setInterval(() => {
      const wasHidden = this.#hiddenByOverlay;
      this.#updateOverlayOffset();
      if (wasHidden !== this.#hiddenByOverlay) {
        if (this.#hiddenByOverlay) this.#hide(true);
        else this.#show();
      } else if (this.#isStuck) {
        this.#updateScrollPadding();
      }
    }, 700);
  }

  #stopOverlayWatch() {
    window.clearInterval(this.#overlayTimer);
    this.#overlayTimer = undefined;
  }

  /**
   * Measure visible fixed overlays docked at the bottom of the viewport.
   * Small ones (< 30% of viewport) push the bar up; tall ones (consent dialogs) hide it.
   */
  #updateOverlayOffset() {
    const vh = window.innerHeight;
    let offset = 0;
    let tall = false;

    for (const selector of BOTTOM_OVERLAY_SELECTORS) {
      document.querySelectorAll(selector).forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const style = getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return;
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        const rect = el.getBoundingClientRect();
        if (rect.height <= 0 || rect.width <= 0) return;
        if (rect.bottom < vh - 4 || rect.top >= vh) return;
        const covered = vh - rect.top;
        if (covered > vh * 0.3) {
          tall = true;
        } else {
          offset = Math.max(offset, Math.ceil(covered));
        }
      });
    }

    this.#hiddenByOverlay = tall;
    this.refs.stickyBar.style.setProperty('--noju-sticky-atc-bottom', `${offset}px`);
  }

  /** @param {MouseEvent} event */
  #onStickyClick = (event) => {
    event.preventDefault();

    const mainButton = this.#getMainButton();
    if (!(mainButton instanceof HTMLButtonElement)) return;

    const mode = mainButton.dataset.nojuAtcMode || 'add';
    if (mode === 'blocked') return;

    if (mode === 'select' || !this.#hasCompleteSizeSelection()) {
      // No size yet: open the size sheet (Tétaro behaviour). No picker: scroll to it.
      this.#lastOpener = this.refs.stickyAtcButton;
      if (!this.#openSheet()) this.#scrollToPicker();
      return;
    }

    if (mainButton.disabled && mode !== 'notify') return;

    this.#clickMain();
  };

  #onSizeButtonClick = () => {
    this.#lastOpener = this.querySelector('[data-noju-sticky-size-button]');
    if (!this.#openSheet()) this.#scrollToPicker();
  };

  #clickMain() {
    const mainButton = this.#getMainButton();
    if (!(mainButton instanceof HTMLButtonElement)) return;
    mainButton.dataset.puppet = 'true';
    mainButton.click();
  }

  /** @returns {HTMLElement | null} */
  #getSheet() {
    const sheet = this.querySelector('[data-noju-sticky-sheet]');
    return sheet instanceof HTMLElement ? sheet : null;
  }

  /** @returns {boolean} whether the sheet opened */
  #openSheet() {
    const sheet = this.#getSheet();
    if (!sheet) return false;
    this.#buildSizeControls();
    this.#syncSizeControls();
    if (!sheet.querySelector('[data-index]')) return false;
    sheet.classList.add('is-open');
    sheet.removeAttribute('inert');
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Hide the Shopify Inbox chat launcher while the sheet is open (it floated above the scrim).
    document.documentElement.classList.add('noju-sb-sheet-open');
    this.#hideChat();
    this.#sheetOpen = true;
    const selected = sheet.querySelector('.noju-sb-chip[aria-pressed="true"]');
    const first = sheet.querySelector('.noju-sb-chip');
    const close = sheet.querySelector('.noju-sb-sheet__close');
    const focusTarget = selected ?? first ?? close;
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
    return true;
  }

  #closeSheet() {
    const sheet = this.#getSheet();
    if (!sheet || !this.#sheetOpen) return;
    const back = this.#lastOpener?.isConnected ? this.#lastOpener : this.refs.stickyAtcButton;
    if (sheet.contains(document.activeElement)) back?.focus({ preventScroll: true });
    sheet.classList.remove('is-open');
    sheet.setAttribute('aria-hidden', 'true');
    sheet.setAttribute('inert', '');
    document.body.style.overflow = '';
    document.documentElement.classList.remove('noju-sb-sheet-open');
    this.#showChat();
    this.#sheetOpen = false;
  }

  /**
   * After a size pick the main buy box re-syncs; act once it is ready.
   * add mode: add to cart. notify mode: open the Notify me dialog.
   */
  #actWhenReady() {
    let tries = 0;
    const tick = () => {
      tries += 1;
      const mainButton = this.#getMainButton();
      const ready =
        mainButton instanceof HTMLButtonElement &&
        this.#hasCompleteSizeSelection() &&
        !mainButton.dataset.nojuAtcState;
      const mode = mainButton instanceof HTMLButtonElement ? mainButton.dataset.nojuAtcMode || 'add' : '';
      if (ready && mode === 'notify') {
        this.#clickMain();
      } else if (ready && mode === 'add' && !mainButton.disabled) {
        window.setTimeout(() => this.#clickMain(), 120);
      } else if (tries < 30) {
        window.setTimeout(tick, 100);
      } else {
        this.#syncFromMainAtc();
      }
    };
    window.setTimeout(tick, 60);
  }

  #scrollToPicker() {
    const section = this.closest('.shopify-section');
    const target =
      section?.querySelector('.noju-pdp__block--sizes') ??
      section?.querySelector('variant-picker.noju-pdp__variant-picker');
    if (!(target instanceof HTMLElement)) return;

    // Sticky header re-appears when scrolling up, so reserve its full height.
    const header = document.querySelector('.header-section') ?? document.querySelector('header');
    const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 0;
    const label = section?.querySelector('.noju-pdp__size-group, .noju-pdp__size-label');
    const anchor = label instanceof HTMLElement && label.getBoundingClientRect().top < target.getBoundingClientRect().top ? label : target;
    const top = window.scrollY + anchor.getBoundingClientRect().top - headerHeight - 24;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });

    target.classList.remove('noju-pdp__block--attention');
    // Restart animation.
    void target.offsetWidth;
    target.classList.add('noju-pdp__block--attention');
    window.clearTimeout(this.#highlightTimer);
    this.#highlightTimer = window.setTimeout(() => {
      target.classList.remove('noju-pdp__block--attention');
    }, 1800);

    const firstAvailable = target.querySelector(
      'fieldset.noju-pdp__size-options input[type="radio"]:not(:checked)'
    );
    if (firstAvailable instanceof HTMLElement) {
      window.setTimeout(() => firstAvailable.focus({ preventScroll: true }), reduce ? 0 : 450);
    }
  }

  /** @param {CustomEvent} _event */
  #onCartComplete = (_event) => {
    const mainButton = this.#getMainButton();
    if (mainButton instanceof HTMLButtonElement) {
      mainButton.dataset.puppet = 'false';
    }
  };

  /** @param {import('./events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail?.data?.productId && event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    }
    const thumb = this.querySelector('[data-noju-sticky-thumb]');
    const src = variant?.featured_media?.preview_image?.src;
    if (thumb instanceof HTMLImageElement && typeof src === 'string' && src) {
      thumb.src = `${src}${src.includes('?') ? '&' : '?'}width=100`;
    }
    this.#syncFromMainAtc();
  };

  /** @param {Event} event */
  #onSectionChange = (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.closest('fieldset.noju-pdp__size-options')) {
      requestAnimationFrame(() => this.#syncFromMainAtc());
    }
  };

  /**
   * Mirror label, mode, disabled state, price and selected size from the main buy box.
   * Called from noju-pdp.js after buybox sync as well.
   */
  syncFromMainAtc() {
    this.#syncFromMainAtc();
  }

  #syncFromMainAtc() {
    const mainButton = this.#getMainButton();
    const stickyButton = this.refs.stickyAtcButton;
    if (!(mainButton instanceof HTMLButtonElement) || !(stickyButton instanceof HTMLButtonElement)) return;

    const container = mainButton.closest('add-to-cart-component') ?? mainButton;
    const mainLabel = container.querySelector('[data-noju-atc-label]');
    const mainPrice = container.querySelector('[data-noju-atc-price]');
    const stickyLabel = this.querySelector('[data-noju-sticky-atc-label]');
    const stickyPrice = this.querySelector('[data-noju-sticky-price]');

    if (!mainButton.dataset.nojuAtcState && mainLabel instanceof HTMLElement && stickyLabel instanceof HTMLElement) {
      stickyLabel.textContent = mainLabel.textContent;
    }

    const priceText = mainPrice instanceof HTMLElement ? mainPrice.textContent?.trim() : '';
    if (priceText && stickyPrice instanceof HTMLElement) {
      stickyPrice.textContent = priceText;
    }

    const mode = mainButton.dataset.nojuAtcMode || 'add';
    stickyButton.dataset.nojuAtcMode = mode;
    // Keep sticky clickable in select mode so it can scroll to the size picker.
    if (!mainButton.dataset.nojuAtcState) {
      stickyButton.disabled = mode === 'blocked';
    }

    this.#syncSize();
    this.#syncSizeControls();
  }

  /** @param {boolean} on */
  #setErrorText(on) {
    let el = this.querySelector('[data-noju-sticky-error]');
    if (on) {
      if (!(el instanceof HTMLElement)) {
        el = document.createElement('p');
        el.className = 'noju-pdp-sticky-atc__error';
        el.setAttribute('data-noju-sticky-error', '');
        el.setAttribute('role', 'alert');
        this.querySelector('.noju-pdp-sticky-atc__info')?.append(el);
      }
      el.textContent = this.dataset.sizeErrorLabel || 'Select your size';
    } else if (el instanceof HTMLElement) {
      el.remove();
    }
  }

  /** @returns {HTMLFieldSetElement[]} */
  #getMainFieldsets() {
    const section = this.closest('.shopify-section');
    return /** @type {HTMLFieldSetElement[]} */ ([
      ...(section?.querySelectorAll('variant-picker.noju-pdp__variant-picker fieldset.noju-pdp__size-options') ?? []),
    ]);
  }

  /**
   * @param {string} index
   * @param {string} value
   */
  #selectMainSize(index, value) {
    const fieldset = this.#getMainFieldsets().find((f) => (f.dataset.fieldsetIndex || '0') === index);
    const input = [...(fieldset?.querySelectorAll('input[type="radio"]') ?? [])].find(
      (i) => i instanceof HTMLInputElement && i.value === value
    );
    if (!(input instanceof HTMLInputElement)) return;
    if (input.checked) {
      // Already checked (e.g. from a URL variant): still counts as a choice.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      input.click();
    }
  }

  /** Build size sheet chips from the main picker (Core Set: one labelled chip row per garment). */
  #buildSizeControls() {
    const host = this.querySelector('[data-noju-sticky-sizes]');
    if (!(host instanceof HTMLElement)) return;
    const fieldsets = this.#getMainFieldsets();
    if (fieldsets.length === 0) return;
    host.replaceChildren();

    const setLabels = [this.dataset.setJacketLabel || 'Jacket', this.dataset.setPantsLabel || 'Pants'];
    const waitLabel = this.dataset.waitlistLabel || 'Notify me';
    /** @param {HTMLInputElement} input */
    const textOf = (input) =>
      input.closest('label')?.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() || input.value;

    host.classList.toggle('noju-sb-sheet__chips--rows', fieldsets.length > 1);

    fieldsets.forEach((fieldset, position) => {
      const index = fieldset.dataset.fieldsetIndex || String(position);
      const inputs = /** @type {HTMLInputElement[]} */ ([...fieldset.querySelectorAll('input[type="radio"]')]);
      let parent = host;
      let rowLabel = '';

      if (fieldsets.length > 1) {
        rowLabel = setLabels[position] || fieldset.querySelector('legend')?.textContent?.trim() || '';
        const row = document.createElement('div');
        row.className = 'noju-sb-sheet__row';
        row.setAttribute('role', 'group');
        row.setAttribute('aria-label', rowLabel);
        const labelEl = document.createElement('p');
        labelEl.className = 'noju-sb-sheet__row-label';
        labelEl.textContent = rowLabel;
        const grid = document.createElement('div');
        grid.className = 'noju-sb-sheet__grid';
        row.append(labelEl, grid);
        host.append(row);
        parent = grid;
      } else {
        const grid = document.createElement('div');
        grid.className = 'noju-sb-sheet__grid';
        host.append(grid);
        parent = grid;
      }

      inputs.forEach((input) => {
        const soldOut = input.dataset.optionAvailable === 'false';
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = `noju-sb-chip${soldOut ? ' noju-sb-chip--wait' : ''}`;
        chip.dataset.index = index;
        chip.dataset.value = input.value;
        chip.textContent = textOf(input);
        chip.setAttribute('aria-pressed', 'false');
        if (rowLabel) chip.setAttribute('aria-label', `${rowLabel} ${textOf(input)}${soldOut ? `, ${waitLabel}` : ''}`);
        if (soldOut) {
          const note = document.createElement('span');
          note.className = 'noju-sb-chip__note';
          note.textContent = waitLabel;
          chip.append(note);
        }
        chip.addEventListener('click', () => this.#onChipClick(index, input.value));
        parent.append(chip);
      });
    });
  }

  /**
   * @param {string} index
   * @param {string} value
   */
  #onChipClick(index, value) {
    this.#selectMainSize(index, value);
    this.#syncSizeControls();
    // Core Set: wait until every garment has a size before closing.
    const complete = this.#getMainFieldsets().every((f) => Boolean(f.querySelector('input[type="radio"]:checked')));
    if (!complete) return;
    this.#closeSheet();
    this.#actWhenReady();
  }

  #syncSizeControls() {
    if (!this.querySelector('[data-noju-sticky-sizes] [data-index]')) this.#buildSizeControls();
    const host = this.querySelector('[data-noju-sticky-sizes]');
    if (!(host instanceof HTMLElement)) return;

    this.#getMainFieldsets().forEach((fieldset, position) => {
      const index = fieldset.dataset.fieldsetIndex || String(position);
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      const value = checked instanceof HTMLInputElement ? checked.value : '';
      host.querySelectorAll(`.noju-sb-chip[data-index="${index}"]`).forEach((chip) => {
        chip.setAttribute('aria-pressed', String(chip instanceof HTMLElement && chip.dataset.value === value));
      });
      const select = host.querySelector(`select[data-index="${index}"]`);
      if (select instanceof HTMLSelectElement && select.value !== value) select.value = value;
    });

    if (this.#hasCompleteSizeSelection()) {
      this.refs.stickyBar.classList.remove('noju-pdp-sticky-atc__bar--error');
      this.#setErrorText(false);
    }
  }

  #syncSize() {
    const sizeEl = this.querySelector('[data-noju-sticky-size]');
    if (!(sizeEl instanceof HTMLElement)) return;

    const section = this.closest('.shopify-section');
    const fieldsets = section?.querySelectorAll('variant-picker.noju-pdp__variant-picker fieldset.noju-pdp__size-options');
    const values = [];
    let complete = Boolean(fieldsets && fieldsets.length);

    fieldsets?.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!(checked instanceof HTMLInputElement)) {
        complete = false;
        return;
      }
      const text =
        checked.closest('label')?.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() ||
        checked.value;
      values.push(text);
    });

    sizeEl.textContent = complete && values.length ? values.join(' / ') : this.dataset.sizeLabel || 'Size';
  }

  #hasCompleteSizeSelection() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker.noju-pdp__variant-picker');
    if (!(picker instanceof HTMLElement)) return true;

    const fieldsets = picker.querySelectorAll('fieldset.noju-pdp__size-options');
    if (fieldsets.length === 0) return true;

    const mainButton = this.#getMainButton();
    if (mainButton instanceof HTMLButtonElement && mainButton.dataset.nojuAtcMode === 'select') return false;

    return Array.from(fieldsets).every((fieldset) => Boolean(fieldset.querySelector('input[type="radio"]:checked')));
  }

  #show() {
    if (!this.#isStuck) {
      this.#syncFromMainAtc();
    }
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
    this.refs.stickyBar.removeAttribute('inert');
    this.#updateScrollPadding();
  }

  /** @param {boolean} [keepWatching] */
  #hide(keepWatching = false) {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
    this.refs.stickyBar.setAttribute('inert', '');
    this.#clearOffsets();
    if (!keepWatching) this.#stopOverlayWatch();
  }

  #updateScrollPadding() {
    const bar = this.refs.stickyBar;
    const height = Math.ceil(bar.getBoundingClientRect().height);
    const bottom = parseInt(bar.style.getPropertyValue('--noju-sticky-atc-bottom') || '0', 10) || 0;
    const total = height + bottom;
    document.documentElement.style.setProperty('--noju-sticky-atc-height', `${total}px`);
    // Keep Shopify chat activator above the sticky bar.
    document.documentElement.style.setProperty('--shopify-chat-activator-offset', `${total + 12}px`);
    document.body.style.paddingBottom = `${height}px`;
  }

  #clearOffsets() {
    document.documentElement.style.removeProperty('--noju-sticky-atc-height');
    document.documentElement.style.removeProperty('--shopify-chat-activator-offset');
    document.body.style.paddingBottom = '';
  }

  #getMainButton() {
    return this.#getProductForm()?.querySelector('[data-noju-atc-button]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    return section.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }
}

if (!customElements.get('noju-pdp-sticky-atc')) {
  customElements.define('noju-pdp-sticky-atc', NojuPdpStickyAtc);
}

/**
 * @param {HTMLElement} section
 */
export function syncNojuPdpStickyAtc(section) {
  const sticky = section.querySelector('noju-pdp-sticky-atc');
  if (sticky && typeof sticky.syncFromMainAtc === 'function') {
    sticky.syncFromMainAtc();
  }
}
